import {CopilotFixDialog} from '@github-ui/copilot-apply-changes-button/CopilotFixDialog'
import type {CopilotFixDialogSubmitOptions} from '@github-ui/copilot-fix-controls/types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ActionList} from '@primer/react'
import {type FC, useCallback, useMemo, useState} from 'react'
import {FINDINGS_ANALYTICS_EVENTS} from './analytics'
import {
  getDisplayFilePaths,
  getFindingRenderableFileLocation,
  getFindingSources,
  getFindingTitle,
} from './entity-helpers'
import styles from './Finding.module.css'
import type {Finding} from './types'

interface FixFindingDialogProps {
  findings: Finding[]
  basePath: string
  headSha: string
  maxFindingBatchSize: number
  modelsUrl?: string | null
  onClose: () => void
  onTaskCreated?: (ccaTaskId: string, taskUrl: string, findingIds: string[]) => void
}

export const FixFindingDialog: FC<FixFindingDialogProps> = ({
  findings,
  basePath,
  headSha,
  maxFindingBatchSize,
  modelsUrl,
  onClose,
  onTaskCreated,
}) => {
  const [selection] = useState(() => ({findings, headSha}))
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(selection.findings.slice(0, maxFindingBatchSize).map(f => f.id)),
  )
  const {sendAnalyticsEvent} = useAnalytics()

  const selectedFindings = useMemo(
    () => selection.findings.filter(f => selectedIds.has(f.id)),
    [selection.findings, selectedIds],
  )
  const displayFilePaths = useMemo(
    () => getDisplayFilePaths(selection.findings.filter(f => f.fileUrl).map(f => f.filePath)),
    [selection.findings],
  )

  const handleToggle = useCallback(
    (id: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else if (next.size < maxFindingBatchSize) {
          next.add(id)
        }
        return next
      })
    },
    [maxFindingBatchSize],
  )

  const handleSubmit = useCallback(
    async ({createPullRequest, instructions, model}: CopilotFixDialogSubmitOptions) => {
      const body: Record<string, unknown> = {
        create_pull_request: createPullRequest,
        expected_head_sha: selection.headSha,
        findings: selectedFindings.map(f => ({
          id: f.id,
          partitionRef: f.partitionRef,
        })),
      }
      if (instructions) body.instructions = instructions
      if (model && model !== 'auto') body.model = model

      const response = await verifiedFetchJSON(`${basePath}/code_review_findings/fix`, {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(data?.error || data?.message || 'Failed to create fix task')
      }

      const data = await response.json()
      if (!data?.task_url) {
        throw new Error('No task URL returned')
      }
      sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.fix.type, FINDINGS_ANALYTICS_EVENTS.fix.target, {
        count: selectedFindings.length,
        model: model ?? 'auto',
        create_pull_request: createPullRequest,
      })
      if (onTaskCreated && data.task_id && data.finding_ids) {
        onTaskCreated(data.task_id, data.task_url, data.finding_ids)
      }
    },
    [basePath, selection.headSha, selectedFindings, onTaskCreated, sendAnalyticsEvent],
  )

  return (
    <CopilotFixDialog
      title="Fix with Copilot"
      onClose={onClose}
      onSubmit={handleSubmit}
      modelsUrl={modelsUrl ?? undefined}
      submitDisabled={selectedIds.size === 0}
      closeOnSubmit
    >
      <div className="tmp-mt-3">
        <p className="tmp-mb-1 text-small text-semibold color-fg-muted">
          <span>{`${selectedIds.size} ${selectedIds.size === 1 ? 'finding' : 'findings'}`}</span>
          {selection.findings.length > maxFindingBatchSize && <span>{` (maximum ${maxFindingBatchSize})`}</span>}
        </p>
        <ActionList
          showDividers
          role="listbox"
          selectionVariant="multiple"
          aria-label="Findings to fix"
          className="mt-2 border rounded-3"
        >
          {selection.findings.map(f => {
            const sources = getFindingSources(f)
            const fileLocation = getFindingRenderableFileLocation(f, displayFilePaths.get(f.filePath))
            const fileLocationFullPathText =
              fileLocation && fileLocation.fullLocation !== fileLocation.displayLocation ? (
                <span className="sr-only">{` Full path: ${fileLocation.fullLocation}`}</span>
              ) : null
            return (
              <ActionList.Item
                key={f.id}
                selected={selectedIds.has(f.id)}
                disabled={!selectedIds.has(f.id) && selectedIds.size >= maxFindingBatchSize}
                onSelect={() => handleToggle(f.id)}
              >
                <span className="text-semibold">{getFindingTitle(f)}</span>
                <ActionList.Description variant="block">
                  <span className="d-flex flex-items-center flex-wrap gap-1 color-fg-muted text-small">
                    {sources.map(source => (
                      <span key={source.key} className={styles.metaItem}>
                        {source.author?.avatarUrl && (
                          <GitHubAvatar alt="" size={20} src={source.author.avatarUrl} square={source.author.isBot} />
                        )}
                        <span className="tmp-ml-1">
                          {source.label}
                          {source.author && <span className="sr-only"> by @{source.author.login}</span>}
                        </span>
                      </span>
                    ))}
                    {fileLocation && (
                      <span className={styles.metaItem}>
                        <span className="text-mono">
                          <span>{fileLocation.displayLocation}</span>
                          {fileLocationFullPathText}
                        </span>
                      </span>
                    )}
                  </span>
                </ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList>
      </div>
    </CopilotFixDialog>
  )
}
